import { z } from 'zod'

import { EVENTS as FLOW_EVENTS } from '@flows/configs/analytics/constants'
import { getPhoneNumberSchema } from '@flows/configs/common/steps/email_and_name'
import type { AnyCallbackConfig, DispatchedHook } from '@flows/core/types/dispatchedHookTypes'
import type { AnySimpleOnSubmitHook } from '@flows/core/types/stepTypes'

export type TrackFn = (event: string, properties?: Record<string, unknown>) => void

type StepContentBlurHook = AnySimpleOnSubmitHook | DispatchedHook<AnyCallbackConfig>

const BLUR_TRACKING = {
  onEmailBlur: {
    schema: z.string().email(),
    event: FLOW_EVENTS.QuoteEmailSubmitted,
    property: 'email',
  },
  onPhoneBlur: {
    schema: getPhoneNumberSchema(),
    event: FLOW_EVENTS.QuotePhoneSubmitted,
    property: 'phone_number',
  },
} as const

export type BlurHookName = keyof typeof BLUR_TRACKING

const isBlurHookName = (name: unknown): name is BlurHookName =>
  typeof name === 'string' && Object.prototype.hasOwnProperty.call(BLUR_TRACKING, name)

/**
 * Retargeting reports the first time a visitor completes a field, so the same value is only
 * ever sent once. The set lives for the page load, as it did in the legacy flows.
 */
const trackedValues = new Set<string>()

/**
 * Runs a step content element's `onBlur` dispatched hook. Only tracking hooks are registered,
 * so an element without one — or with an unknown hook name — is a no-op.
 */
export const runOnBlurHook = ({
  onBlur,
  value,
  track,
}: {
  onBlur?: StepContentBlurHook
  value: unknown
  track?: TrackFn
}) => {
  if (!track || onBlur?.type !== 'dispatchedHook') {
    return
  }

  const hookName = onBlur.callbackConfig?.name
  if (!isBlurHookName(hookName)) {
    return
  }

  const tracking = BLUR_TRACKING[hookName]
  const parsed = tracking.schema.safeParse(value)
  if (!parsed.success) {
    return
  }

  const key = `${tracking.property}:${parsed.data}`
  if (trackedValues.has(key)) {
    return
  }
  trackedValues.add(key)

  track(tracking.event, {
    product_id: onBlur.callbackConfig?.options?.productKey,
    [tracking.property]: parsed.data,
  })
}
